/**
 * The dial sequence model for the phone call widget's caller-ID/extension setup.
 *
 * A `DialSequence` is what the vet configures in the UI (a list of "wait N seconds" /
 * "press these digits" steps). `compile()` turns it into the flat comma/digit string
 * stored as the row's `extension`, where each comma is a 2s pause (expanded to Twilio's
 * `wwww` server-side). `parse()` is the inverse, used to render a stored string back into
 * steps for editing/preview.
 *
 * Intentionally duplicated in API-Server (`app/domain/dialSequence.ts`, `compile()` only —
 * the server only needs to be authoritative over the compiled value, it never renders
 * prose). Do not extract into a shared package without a `NODE_AUTH_TOKEN` story; keep the
 * two copies' `compile` behavior identical by hand until then. `parse()` has no second
 * implementation, so it cannot drift at all.
 *
 * Each repo asserts a shared test-vector table against its OWN `compile()` in CI, so a
 * behavior change in one copy that its own expectations don't reflect fails that repo's
 * build. Nothing covers the cross-repo half: a correct, self-consistent change landing in
 * only one repo fails neither CI. Parity evidence and the mechanical drift check:
 * specs/2026-08-25-en1050-caller-id-setup-design.md §13.1.
 */
export type DialStep = { kind: "wait"; seconds: number } | { kind: "press"; digits: string };

export type DialSequence = DialStep[];

/**
 * Seconds offered in the "wait" picker. Even values only: `compile()` encodes a wait as
 * one comma per 2 seconds, so an odd value cannot round-trip losslessly through the
 * stored `extension` string. `0` is the deliberate "Don't wait" choice and must stay —
 * it compiles to nothing and is dropped by `describe`/`describeShort`.
 */
export const WAIT_OPTIONS: number[] = [0, 2, 4, 6, 8, 10, 12, 14, 16, 20];

/** Compiles a `DialSequence` into the flat string stored as the account's extension. */
export const compile = (steps: DialSequence): string =>
    steps.map((step) => (step.kind === "wait" ? ",".repeat(Math.floor(step.seconds / 2)) : step.digits)).join("");

// Matches a run of commas (a wait) or a run of dial-pad characters (a press) at a time.
// Any other character (this runs against legacy/hand-edited rows) simply doesn't match
// either alternative and is skipped, per parse()'s "total function" contract below.
const TOKEN_PATTERN = /(,+)|([0-9*#]+)/g;

/**
 * Parses a stored extension string back into steps. Total function: characters that are
 * neither a comma nor a dial-pad character are silently dropped rather than throwing,
 * since this can run against rows this feature did not write.
 */
export const parse = (raw: string): DialStep[] => {
    const steps: DialStep[] = [];
    const pattern = new RegExp(TOKEN_PATTERN.source, TOKEN_PATTERN.flags);
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(raw)) !== null) {
        const [, commas, digits] = match;
        if (commas) {
            steps.push({ kind: "wait", seconds: commas.length * 2 });
        } else if (digits) {
            steps.push({ kind: "press", digits });
        }
    }
    return steps;
};

/** Total seconds the sequence takes, matching Twilio's `w` pause unit: 0.5s per digit pressed. */
export const totalSeconds = (steps: DialSequence): number =>
    steps.reduce((total, step) => total + (step.kind === "wait" ? step.seconds : step.digits.length * 0.5), 0);

/**
 * Server-side caps this model must not exceed, mirrored from API-Server's `DialStepSchema` /
 * `DialStepsSchema` (`app/domain/phoneSchemas.ts`). They live here, next to the model, so the
 * builder can refuse to produce something the server will reject rather than letting the vet fill
 * in a valid-looking form and collect a 400 on Save.
 *
 * Mirrored, not derived — there is no shared package (see this file's header) — so an edit on
 * either side needs the other. `dialSequenceProblem` below is the single reader.
 */
export const MAX_DIAL_STEPS = 20; // DialStepsSchema: z.array(...).max(20)
export const MAX_PRESS_DIGITS = 32; // DialStepSchema.digits: .max(32)
export const MAX_WAIT_SECONDS = 120; // DialStepSchema.seconds: .max(120)

/**
 * EN-1005 measured Twilio validation-call IVR timeout: how long the call waits for DTMF before
 * hanging up. **Observed, not documented** — do not treat it as a specified constant.
 */
export const TWILIO_IVR_TIMEOUT_SECONDS = 40;

/**
 * Seconds reserved out of the IVR timeout for someone to actually enter the 6-digit code, rather
 * than treating all 40 seconds as available for dialing.
 *
 * 10 seconds, reasoned rather than measured: the code is 6 digits, and the person at the handset
 * has to register that the prompt has started, read the code off a screen they may not be holding,
 * and press six keys. Six deliberate keypresses on an unfamiliar handset is roughly 6 seconds, and
 * the remaining ~4 covers noticing the prompt. Erring high is the cheap direction — a sequence
 * rejected as too long costs the vet one edit, whereas one accepted too generously costs a
 * verification attempt that can never succeed, which is the exact failure this ticket exists to
 * remove.
 */
export const CODE_ENTRY_MARGIN_SECONDS = 10;

/** The longest sequence that can still leave room to enter the code. */
export const MAX_SEQUENCE_SECONDS = TWILIO_IVR_TIMEOUT_SECONDS - CODE_ENTRY_MARGIN_SECONDS;

/**
 * The reason this sequence cannot be saved, as copy for the vet, or `null` when it can.
 *
 * Two jobs, deliberately in one function so the form's submit gate and the message on screen can
 * never disagree: the UI disables Save & Verify exactly when this is non-null, and renders exactly
 * this string. §7 of the design is explicit that Save is never disabled without a message on
 * screen, and one source for both is how that stays true.
 *
 * Order matters. The structural caps come first because they are about what can be stored at all,
 * and their copy names a specific step the vet can go fix; the timing budget is reported last and
 * is the only one reachable from a sequence built entirely out of the dropdowns.
 */
export const dialSequenceProblem = (steps: DialSequence): string | null => {
    if (steps.length > MAX_DIAL_STEPS) {
        return `A dial sequence can have at most ${MAX_DIAL_STEPS} steps. Remove ${steps.length - MAX_DIAL_STEPS} to save.`;
    }

    const longPress = steps.findIndex((step) => step.kind === "press" && step.digits.length > MAX_PRESS_DIGITS);
    if (longPress !== -1) {
        return `Step ${longPress + 1} dials more than ${MAX_PRESS_DIGITS} characters. Shorten it to save.`;
    }

    // Only reachable from a stored value: the dropdown never offers anything above 20s, but a
    // legacy comma string is unbounded (61+ consecutive commas is a 122s wait) and the widened
    // dropdown deliberately preserves such a value rather than clamping it away.
    const longWait = steps.findIndex((step) => step.kind === "wait" && step.seconds > MAX_WAIT_SECONDS);
    if (longWait !== -1) {
        return `Step ${longWait + 1} waits longer than ${MAX_WAIT_SECONDS} seconds, which is more than we can save. Shorten it.`;
    }

    // The blocking half of the timeout story. A sequence this long can NEVER verify — the call
    // hangs up before the code can be entered — so saving it is saving a guaranteed failure. The
    // sub-threshold advisory (see DialSequenceField) stays advisory; this is the point where it
    // stops being a suggestion.
    if (totalSeconds(steps) > MAX_SEQUENCE_SECONDS) {
        return `This sequence takes longer than the ${TWILIO_IVR_TIMEOUT_SECONDS}-second call will wait, so the code could never be entered. Shorten it to under ${MAX_SEQUENCE_SECONDS} seconds.`;
    }

    return null;
};

/**
 * Builds the ordered, human-readable clauses for a sequence (shared by `describe` and
 * `describeShort` so the two only differ in wording, not in ordering logic).
 *
 * Zero-second waits and empty presses are dropped first. Among the remaining press
 * steps, the *last* one is labeled "dial" (it's the number actually reached); any
 * earlier presses are labeled "press" (intermediate menu navigation). This is
 * independent of whether a wait step follows the last press — e.g. "dial 101, then
 * wait 2 seconds" for a press followed by a trailing pause.
 */
const buildClauses = (steps: DialSequence, formatWait: (seconds: number) => string): string[] => {
    const relevant = steps.filter((step) => (step.kind === "wait" ? step.seconds > 0 : step.digits.length > 0));

    let lastPressIndex = -1;
    for (let i = relevant.length - 1; i >= 0; i--) {
        if (relevant[i].kind === "press") {
            lastPressIndex = i;
            break;
        }
    }

    return relevant.map((step, i) => {
        if (step.kind === "wait") {
            return `wait ${formatWait(step.seconds)}`;
        }
        return `${i === lastPressIndex ? "dial" : "press"} ${step.digits}`;
    });
};

// Joins clauses "A, B, then C." (single clause: "A." with no "then"; empty: "").
const joinClauses = (clauses: string[], lead: string, suffix: string): string => {
    if (clauses.length === 0) return "";
    if (clauses.length === 1) return `${lead}${clauses[0]}${suffix}`;
    const allButLast = clauses.slice(0, -1).join(", ");
    const last = clauses[clauses.length - 1];
    return `${lead}${allButLast}, then ${last}${suffix}`;
};

/** Long-form prose description, e.g. `"We'll wait 8 seconds, press 2, then dial 101."` */
export const describe = (steps: DialSequence): string =>
    joinClauses(
        buildClauses(steps, (s) => `${s} second${s === 1 ? "" : "s"}`),
        "We'll ",
        ".",
    );

/** Compact description for tight UI space, e.g. `"wait 8s, press 2, then dial 101"`. */
export const describeShort = (steps: DialSequence): string =>
    joinClauses(
        buildClauses(steps, (s) => `${s}s`),
        "",
        "",
    );
